/**
 * SectionSubTabs — secondary in-page tab bar (horizontal top navigation).
 *
 * Renders horizontal tabs for the children of the navigation section the user
 * is currently in. Tabs that have children open a dropdown menu instead of
 * navigating. Returns null outside a registered section.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import {
  findSectionForPath,
  type NavigationItem,
  type NavigationSection,
} from '../registry/portalRegistry';

export const SectionSubTabs: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [subMenuAnchor, setSubMenuAnchor] = useState<null | HTMLElement>(null);
  const [subMenuItem, setSubMenuItem] = useState<NavigationItem | null>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);

  const currentSection = useMemo<NavigationSection | undefined>(
    () => findSectionForPath(location.pathname),
    [location.pathname],
  );

  const tabs = currentSection?.children ?? [];

  // Find the active tab — prefer the deepest matching prefix.
  const activeTabIndex = useMemo(() => {
    if (!tabs.length) return -1;
    let bestIdx = -1;
    let bestLen = 0;
    const considerPath = (i: number, path: string) => {
      if (location.pathname === path) {
        bestIdx = i;
        bestLen = Number.POSITIVE_INFINITY;
        return;
      }
      if (location.pathname.startsWith(path + '/') && path.length > bestLen) {
        bestIdx = i;
        bestLen = path.length;
      }
    };
    tabs.forEach((tab, i) => {
      considerPath(i, tab.path);
      tab.children?.forEach((child) => considerPath(i, child.path));
    });
    return bestIdx >= 0 ? bestIdx : 0;
  }, [tabs, location.pathname]);

  if (!currentSection) return null;

  const handleTabClick = (e: React.SyntheticEvent, idx: number) => {
    const tab = tabs[idx];
    if (!tab) return;
    if (tab.children?.length) {
      setSubMenuItem(tab);
      setSubMenuAnchor(e.currentTarget as HTMLElement);
    } else {
      navigate(tab.path);
    }
  };

  const handleTabDirectClick = (e: React.MouseEvent, tab: NavigationItem) => {
    if (tab.children?.length) {
      setSubMenuItem(tab);
      setSubMenuAnchor(e.currentTarget as HTMLElement);
      e.stopPropagation();
    }
  };

  const handleSubItemClick = (path: string) => {
    navigate(path);
    setSubMenuAnchor(null);
    setSubMenuItem(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: 40,
        bgcolor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 2,
      }}
    >
      <Chip
        label={currentSection.label}
        size="small"
        color="primary"
        variant="outlined"
        sx={{
          mr: 2,
          fontWeight: 500,
          '& .MuiChip-label': { px: 1 },
        }}
      />

      {!isMobile ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          <Tabs
            value={activeTabIndex >= 0 ? activeTabIndex : false}
            onChange={handleTabClick}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
                height: 2,
              },
              '& .MuiTab-root': {
                minHeight: 40,
                py: 0,
                px: 2,
                textTransform: 'none',
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.text.primary,
                },
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.path}
                onClick={(e) => handleTabDirectClick(e, tab)}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" noWrap>{tab.label}</Typography>
                    {tab.children?.length ? <ChevronDown size={14} /> : null}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      ) : (
        <Box sx={{ flex: 1 }}>
          <IconButton
            onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
            sx={{ color: theme.palette.text.primary }}
          >
            <Typography variant="body2" sx={{ mr: 0.5 }}>
              {tabs[activeTabIndex]?.label || 'Select'}
            </Typography>
            <ChevronDown size={14} />
          </IconButton>
        </Box>
      )}

      {/* Sub-tab menu (for tabs that have children) */}
      <Menu
        anchorEl={subMenuAnchor}
        open={Boolean(subMenuAnchor)}
        onClose={() => { setSubMenuAnchor(null); setSubMenuItem(null); }}
        PaperProps={{ sx: { maxHeight: 300, minWidth: 200 } }}
      >
        {subMenuItem?.children?.map((child) => (
          <MenuItem
            key={child.path}
            onClick={() => handleSubItemClick(child.path)}
            selected={location.pathname === child.path}
          >
            <Typography variant="body2">{child.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Mobile overflow menu */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
        PaperProps={{ sx: { maxHeight: 400, minWidth: 220 } }}
      >
        {tabs.map((tab) => (
          <React.Fragment key={tab.path}>
            <MenuItem
              onClick={() => {
                if (tab.children?.length) navigate(tab.children[0].path);
                else navigate(tab.path);
                setMoreMenuAnchor(null);
              }}
              selected={location.pathname.startsWith(tab.path)}
              sx={{ fontWeight: 500 }}
            >
              {tab.label}
            </MenuItem>
            {tab.children?.map((child) => (
              <MenuItem
                key={child.path}
                onClick={() => { navigate(child.path); setMoreMenuAnchor(null); }}
                selected={location.pathname === child.path}
                sx={{ pl: 4 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {child.label}
                </Typography>
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </Menu>
    </Box>
  );
};

export default SectionSubTabs;
